import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';
import {
  FolderOpen, FileText, Image as ImageIcon, FileSpreadsheet,
  Download, Eye, Trash2, Upload, Search, Plus, MoreVertical,
  Loader2, Tag, Clock, History, AlertTriangle, X, Check,
  ChevronDown, Calendar, Send
} from 'lucide-react';
import PDFPreviewModal from '@/components/modals/PDFPreviewModal';
import SendDocumentDialog from '@/components/documents/SendDocumentDialog';
import FolderTree from '@/components/documents/FolderTree';
import DocumentPreviewPane from '@/components/documents/DocumentPreviewPane';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import { Sparkles, FolderInput, Trash } from 'lucide-react';
import TabExplainer from '@/components/common/TabExplainer';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = [
  "Compliance", "Tax", "Legal", "Personal", "Financial",
  "Insurance", "Estate Planning", "Corporate", "Trust", "SMSF", "Other"
];

const PRESET_TAGS = [
  "Review Required", "Expiring Soon", "Signed", "Draft",
  "Confidential", "Archived", "Priority", "Annual"
];

const getFileIcon = (filename) => {
  const ext = filename?.split('.').pop()?.toLowerCase();
  switch (ext) {
    case 'pdf': return <FileText className="w-5 h-5 text-red-500" />;
    case 'doc': case 'docx': return <FileText className="w-5 h-5 text-blue-500" />;
    case 'xls': case 'xlsx': return <FileSpreadsheet className="w-5 h-5 text-green-500" />;
    case 'jpg': case 'jpeg': case 'png': case 'gif':
      return <ImageIcon className="w-5 h-5 text-purple-500" />;
    default: return <FileText className="w-5 h-5 text-[#8C8078]" />;
  }
};

const formatFileSize = (bytes) => {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const formatDate = (dateString) => {
  if (!dateString) return '—';
  return new Date(dateString).toLocaleDateString('en-AU', {
    day: 'numeric', month: 'short', year: 'numeric'
  });
};

const isExpiringSoon = (expiryDate) => {
  if (!expiryDate) return false;
  const days = (new Date(expiryDate) - new Date()) / (1000 * 60 * 60 * 24);
  return days <= 30 && days > 0;
};

const isExpired = (expiryDate) => {
  if (!expiryDate) return false;
  return new Date(expiryDate) < new Date();
};

export default function ClientDocuments() {
  const { clientId } = useParams();
  const { token } = useAuth();
  const fileInputRef = useRef(null);
  const versionInputRef = useRef(null);

  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [tagFilter, setTagFilter] = useState('all');
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [pendingFile, setPendingFile] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [editDoc, setEditDoc] = useState(null);
  const [versionHistory, setVersionHistory] = useState(null);
  const [versionDoc, setVersionDoc] = useState(null);
  const [sendDoc, setSendDoc] = useState(null);

  // Folder navigation — '__all__' = all docs (default), '__unfiled__' = no folder, or a fld-xxx id
  const [selectedFolderId, setSelectedFolderId] = useState('__all__');
  const [folderRefresh, setFolderRefresh] = useState(0);
  const [folderMap, setFolderMap] = useState({});  // id → folder object for breadcrumb

  // Upload form state
  const [uploadCategory, setUploadCategory] = useState('Other');
  const [uploadTags, setUploadTags] = useState([]);
  const [uploadExpiry, setUploadExpiry] = useState('');
  const [uploadNotes, setUploadNotes] = useState('');
  const [aiSuggestion, setAiSuggestion] = useState(null);   // {category, tags, confidence, reason, ai}
  const [aiLoading, setAiLoading] = useState(false);

  // Bulk multi-select state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulkMove, setShowBulkMove] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  // Inline preview pane
  const [previewDoc, setPreviewDoc] = useState(null);

  const headers = { Authorization: `Bearer ${token}` };

  const fetchDocuments = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const params = {};
      if (selectedFolderId === '__unfiled__') params.folder_id = 'null';
      else if (selectedFolderId && selectedFolderId !== '__all__') params.folder_id = selectedFolderId;

      const res = await axios.get(
        `${API_BASE}/api/clients/${clientId}/documents`,
        { headers, params },
      );
      setDocuments(res.data || []);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
    } finally {
      setLoading(false);
    }
  }, [token, clientId, selectedFolderId]);

  useEffect(() => { fetchDocuments(); }, [fetchDocuments, folderRefresh]);

  // Bump folderRefresh to trigger a re-fetch — used after folder mutations
  // and bulk doc-moves. We avoid calling fetchDocuments() directly here so
  // the react-hooks/set-state-in-effect lint stays clean.
  const handleFolderTreeMutation = useCallback(() => {
    setFolderRefresh((n) => n + 1);
  }, []);

  // Keep a folder-id → folder map for breadcrumb resolution
  useEffect(() => {
    if (!token) return;
    axios.get(`${API_BASE}/api/clients/${clientId}/folders`, { headers })
      .then((r) => {
        const map = {};
        (r.data?.folders || []).forEach((f) => { map[f.id] = f; });
        setFolderMap(map);
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, clientId, folderRefresh]);

  // File selection handler
  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File too large. Maximum 50MB.');
      return;
    }
    setPendingFile(file);
    setUploadCategory('Other');
    setUploadTags([]);
    setUploadExpiry('');
    setUploadNotes('');
    setAiSuggestion(null);
    setShowUploadModal(true);
    e.target.value = '';

    // Fire-and-forget AI classification — pre-fill category + tags
    setAiLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/documents/classify-suggestion`,
        { filename: file.name },
        { headers },
      );
      const s = res.data || {};
      setAiSuggestion(s);
      if (s.category) setUploadCategory(s.category);
      if (Array.isArray(s.tags) && s.tags.length) setUploadTags(s.tags);
    } catch {
      /* silent — AI suggestion is best-effort */
    } finally {
      setAiLoading(false);
    }
  };

  // Upload file
  const handleUpload = async (parentDocId = '') => {
    if (!pendingFile) return;
    setUploading(true);
    setUploadProgress(0);

    const formData = new FormData();
    formData.append('file', pendingFile);
    formData.append('category', uploadCategory);
    formData.append('tags', uploadTags.join(','));
    formData.append('expiry_date', uploadExpiry);
    formData.append('notes', uploadNotes);
    if (parentDocId) formData.append('parent_doc_id', parentDocId);
    // If the user is currently viewing a specific folder, file the upload there.
    if (selectedFolderId && !['__all__', '__unfiled__'].includes(selectedFolderId)) {
      formData.append('folder_id', selectedFolderId);
    }

    try {
      await axios.post(
        `${API_BASE}/api/clients/${clientId}/documents/upload`,
        formData,
        {
          headers: { ...headers, 'Content-Type': 'multipart/form-data' },
          onUploadProgress: (e) => {
            setUploadProgress(Math.round((e.loaded * 100) / e.total));
          }
        }
      );
      toast.success(parentDocId ? 'New version uploaded' : 'Document uploaded');
      setShowUploadModal(false);
      setPendingFile(null);
      setVersionDoc(null);
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Download file
  const handleDownload = async (doc) => {
    try {
      const res = await axios.get(
        `${API_BASE}/api/clients/${clientId}/documents/${doc.id}/download`,
        { headers, responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.name;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Download failed');
    }
  };

  // Preview PDF
  const handlePreview = async (doc) => {
    try {
      const res = await axios.get(
        `${API_BASE}/api/clients/${clientId}/documents/${doc.id}/download`,
        { headers, responseType: 'blob' }
      );
      const url = URL.createObjectURL(res.data);
      setPdfPreview({ url, name: doc.name });
    } catch {
      toast.error('Preview failed');
    }
  };

  // Delete
  const handleDelete = async (doc) => {
    if (!window.confirm(`Delete "${doc.name}"? This action cannot be undone.`)) return;
    try {
      await axios.delete(
        `${API_BASE}/api/clients/${clientId}/documents/${doc.id}`,
        { headers }
      );
      toast.success('Document deleted');
      fetchDocuments();
    } catch {
      toast.error('Delete failed');
    }
  };

  // Update metadata
  const handleUpdateDoc = async () => {
    if (!editDoc) return;
    try {
      await axios.put(
        `${API_BASE}/api/clients/${clientId}/documents/${editDoc.id}`,
        {
          name: editDoc.name,
          category: editDoc.category,
          tags: editDoc.tags,
          expiryDate: editDoc.expiryDate || null,
          notes: editDoc.notes || null
        },
        { headers }
      );
      toast.success('Document updated');
      setEditDoc(null);
      fetchDocuments();
    } catch {
      toast.error('Update failed');
    }
  };

  // Fetch version history
  const handleViewVersions = async (doc) => {
    try {
      const res = await axios.get(
        `${API_BASE}/api/clients/${clientId}/documents/${doc.id}/versions`,
        { headers }
      );
      setVersionHistory({ doc, versions: res.data || [] });
    } catch {
      toast.error('Failed to load versions');
    }
  };

  // New version upload
  const handleNewVersion = (doc) => {
    setVersionDoc(doc);
    versionInputRef.current?.click();
  };

  const handleVersionFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast.error('File too large. Maximum 50MB.');
      return;
    }
    setPendingFile(file);
    setUploadCategory(versionDoc?.category || 'Other');
    setUploadTags(versionDoc?.tags || []);
    setUploadExpiry(versionDoc?.expiryDate || '');
    setUploadNotes('');
    setAiSuggestion(null);  // versions inherit metadata — no AI suggestion
    setShowUploadModal(true);
    e.target.value = '';
  };

  // ============ BULK SELECTION ============
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelectedIds(new Set());
  const allVisibleSelected = (visible) =>
    visible.length > 0 && visible.every((d) => selectedIds.has(d.id));
  const toggleSelectAll = (visible) => {
    setSelectedIds((prev) => {
      if (visible.length > 0 && visible.every((d) => prev.has(d.id))) {
        const next = new Set(prev);
        visible.forEach((d) => next.delete(d.id));
        return next;
      }
      const next = new Set(prev);
      visible.forEach((d) => next.add(d.id));
      return next;
    });
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    if (!window.confirm(`Delete ${ids.length} document(s)? This action cannot be undone.`)) return;
    setBulkBusy(true);
    try {
      const res = await axios.post(
        `${API_BASE}/api/clients/${clientId}/documents/bulk-delete`,
        { documentIds: ids },
        { headers },
      );
      toast.success(`Deleted ${res.data?.deleted ?? ids.length} document(s)`);
      clearSelection();
      if (previewDoc && ids.includes(previewDoc.id)) setPreviewDoc(null);
      fetchDocuments();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk delete failed');
    } finally {
      setBulkBusy(false);
    }
  };

  const handleBulkMove = async (targetFolderId) => {
    const ids = Array.from(selectedIds);
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      await axios.post(
        `${API_BASE}/api/clients/${clientId}/documents/move`,
        { documentIds: ids, folderId: targetFolderId || null },
        { headers },
      );
      toast.success(`Moved ${ids.length} document(s)`);
      clearSelection();
      setShowBulkMove(false);
      handleFolderTreeMutation();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk move failed');
    } finally {
      setBulkBusy(false);
    }
  };

  // Toggle tag
  const toggleTag = (tag, setter, current) => {
    setter(current.includes(tag) ? current.filter(t => t !== tag) : [...current, tag]);
  };

  // Filter documents
  const filteredDocs = documents.filter(doc => {
    const matchSearch = !searchQuery ||
      doc.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      doc.notes?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchCategory = categoryFilter === 'all' || doc.category === categoryFilter;
    const matchTag = tagFilter === 'all' || doc.tags?.includes(tagFilter);
    return matchSearch && matchCategory && matchTag;
  });

  // Stats
  const expiringCount = documents.filter(d => isExpiringSoon(d.expiryDate)).length;
  const expiredCount = documents.filter(d => isExpired(d.expiryDate)).length;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-[#4A3B32]" />
      </div>
    );
  }

  // Build breadcrumb for the current folder
  const breadcrumb = (() => {
    if (selectedFolderId === '__all__') return [{ label: 'All documents' }];
    if (selectedFolderId === '__unfiled__') return [{ label: 'Unfiled' }];
    const chain = [];
    let cursor = folderMap[selectedFolderId];
    while (cursor) {
      chain.unshift({ id: cursor.id, label: cursor.name });
      cursor = cursor.parentId ? folderMap[cursor.parentId] : null;
    }
    return chain.length ? chain : [{ label: 'Folder' }];
  })();

  return (
    <div
      className={cn(
        'grid grid-cols-1 gap-5',
        'lg:grid-cols-[260px_1fr]',
        previewDoc && 'xl:grid-cols-[260px_1fr_360px]'
      )}
      data-testid="client-documents"
    >
      {/* Folder sidebar */}
      <aside className="bg-white border border-[#E6E0D9] rounded-2xl p-3 lg:sticky lg:top-4 lg:self-start lg:max-h-[calc(100vh-6rem)] overflow-y-auto" data-testid="documents-sidebar">
        <FolderTree
          clientId={clientId}
          token={token}
          selectedId={selectedFolderId}
          onSelect={setSelectedFolderId}
          refreshSignal={folderRefresh}
          onDocumentsChanged={handleFolderTreeMutation}
        />
      </aside>

      <div className="space-y-6 min-w-0">
      {/* Hidden file inputs */}
      <input ref={fileInputRef} type="file" className="hidden" onChange={handleFileSelect}
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.zip"
        data-testid="file-input" />
      <input ref={versionInputRef} type="file" className="hidden" onChange={handleVersionFileSelect}
        accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv,.jpg,.jpeg,.png,.gif,.zip" />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <nav className="flex items-center gap-1 text-xs text-[#8C8078] mb-1" data-testid="documents-breadcrumb">
            {breadcrumb.map((c, i) => (
              <span key={c.id || c.label} className="flex items-center gap-1">
                {i > 0 && <span className="text-[#E6E0D9]">/</span>}
                {c.id ? (
                  <button onClick={() => setSelectedFolderId(c.id)} className="hover:text-[#A86A3A]">{c.label}</button>
                ) : (
                  <span>{c.label}</span>
                )}
              </span>
            ))}
          </nav>
          <h2 className="text-2xl font-heading text-[#2C2420]">Documents</h2>
          <div className="flex items-center gap-2 mt-0.5">
            <p className="text-xs text-[#8C8078]">
              Files held in QIndex One &mdash; indexed, versioned, portal-shareable.
            </p>
            <TabExplainer
              label="About Documents"
              testId="documents-tab-explainer"
              title="What is Documents?"
              intro="Files uploaded into QIndex One — under your control, AI-categorised, version-tracked and available to portal users."
              useFor={[
                'Wills, trust deeds, signed contracts, advice statements',
                'ID docs, tax returns (BAS/IAS), compliance evidence',
                'Anything you want owned, secured, version-controlled and shareable into the portal',
              ]}
              notFor={[
                'Files living in another system you only need to reference',
                'Live external statements (bank, broker, super fund portals)',
              ]}
            />
          </div>
          <p className="text-sm text-[#5D524D] mt-2">
            {documents.length} document{documents.length !== 1 ? 's' : ''}
            {expiringCount > 0 && (
              <span className="ml-2 text-amber-600 font-medium">
                {expiringCount} expiring soon
              </span>
            )}
            {expiredCount > 0 && (
              <span className="ml-2 text-red-600 font-medium">
                {expiredCount} expired
              </span>
            )}
          </p>
        </div>
        <Button
          onClick={() => fileInputRef.current?.click()}
          className="bg-[#4A3B32] hover:bg-[#3A2B22] gap-2"
          data-testid="upload-document-btn"
        >
          <Upload className="w-4 h-4" />
          Upload Document
        </Button>
      </div>

      {/* Expiry Alerts Banner */}
      {(expiringCount > 0 || expiredCount > 0) && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm">
            {expiredCount > 0 && (
              <p className="text-red-700 font-medium">
                {expiredCount} document{expiredCount > 1 ? 's have' : ' has'} expired
              </p>
            )}
            {expiringCount > 0 && (
              <p className="text-amber-700">
                {expiringCount} document{expiringCount > 1 ? 's' : ''} expiring within 30 days
              </p>
            )}
          </div>
        </div>
      )}

      {/* Search and Filters */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8C8078]" />
          <Input
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
            data-testid="documents-search"
          />
        </div>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-full sm:w-[160px]" data-testid="category-filter">
            <SelectValue placeholder="Category" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={tagFilter} onValueChange={setTagFilter}>
          <SelectTrigger className="w-full sm:w-[160px]" data-testid="tag-filter">
            <SelectValue placeholder="Tag" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Tags</SelectItem>
            {PRESET_TAGS.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && (
        <div
          className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 rounded-lg border border-[#4A3B32]/20 bg-[#4A3B32]/[0.04] shadow-sm sticky top-2 z-10"
          data-testid="bulk-action-bar"
        >
          <p className="text-sm text-[#2C2420] font-medium">
            {selectedIds.size} document{selectedIds.size > 1 ? 's' : ''} selected
          </p>
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="outline"
              onClick={() => setShowBulkMove(true)}
              disabled={bulkBusy}
              className="gap-1"
              data-testid="bulk-move-btn"
            >
              <FolderInput className="w-3.5 h-3.5" />
              Move to folder
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleBulkDelete}
              disabled={bulkBusy}
              className="gap-1 text-red-600 border-red-200 hover:bg-red-50"
              data-testid="bulk-delete-btn"
            >
              {bulkBusy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trash className="w-3.5 h-3.5" />}
              Delete
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={clearSelection}
              data-testid="bulk-clear-btn"
            >
              Clear
            </Button>
          </div>
        </div>
      )}

      {/* Documents Table */}
      {filteredDocs.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-[#E6E0D9] rounded-lg"
             data-testid="empty-documents">
          <FolderOpen className="w-12 h-12 text-[#8C8078] mx-auto mb-4" />
          <h3 className="font-medium text-[#2C2420] mb-2">No Documents</h3>
          <p className="text-[#5D524D] text-sm mb-4">
            {searchQuery ? 'No documents match your search.' : 'Upload documents to get started.'}
          </p>
          {!searchQuery && (
            <Button onClick={() => fileInputRef.current?.click()}
              className="bg-[#4A3B32] hover:bg-[#3A2B22] gap-2">
              <Upload className="w-4 h-4" /> Upload First Document
            </Button>
          )}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-[#E6E0D9] overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead className="bg-[#F9F7F5] border-b border-[#E6E0D9]">
              <tr>
                <th className="w-10 px-3 py-3">
                  <input
                    type="checkbox"
                    aria-label="Select all visible documents"
                    checked={allVisibleSelected(filteredDocs)}
                    onChange={() => toggleSelectAll(filteredDocs)}
                    className="h-4 w-4 rounded border-[#E6E0D9] text-[#4A3B32] focus:ring-[#4A3B32]"
                    data-testid="select-all-checkbox"
                  />
                </th>
                <th className="text-left px-4 py-3 text-sm font-medium text-[#5D524D]">Document</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-[#5D524D] hidden md:table-cell">Category</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-[#5D524D] hidden lg:table-cell">Tags</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-[#5D524D] hidden sm:table-cell">Size</th>
                <th className="text-left px-4 py-3 text-sm font-medium text-[#5D524D] hidden lg:table-cell">Uploaded</th>
                <th className="text-right px-4 py-3 text-sm font-medium text-[#5D524D]">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredDocs.map((doc) => {
                const isSelected = selectedIds.has(doc.id);
                const isPreviewed = previewDoc?.id === doc.id;
                return (
                <tr
                  key={doc.id}
                  draggable
                  onDragStart={(e) => {
                    // If the dragged row is part of the selection, carry all selected IDs;
                    // otherwise drag just this one row.
                    const ids = selectedIds.has(doc.id) ? Array.from(selectedIds) : [doc.id];
                    e.dataTransfer.setData('application/x-qindex-document', JSON.stringify(ids));
                    e.dataTransfer.effectAllowed = 'move';
                  }}
                  className={cn(
                    "border-b border-[#E6E0D9] last:border-0 hover:bg-[#F9F7F5]/50 cursor-move",
                    isPreviewed && "bg-[#A86A3A]/[0.06]",
                    isSelected && "bg-[#4A3B32]/[0.03]"
                  )}
                  data-testid={`doc-row-${doc.id}`}>
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      aria-label={`Select ${doc.name}`}
                      checked={isSelected}
                      onChange={() => toggleSelect(doc.id)}
                      className="h-4 w-4 rounded border-[#E6E0D9] text-[#4A3B32] focus:ring-[#4A3B32]"
                      data-testid={`select-doc-${doc.id}`}
                    />
                  </td>
                  <td
                    className="px-4 py-3 cursor-pointer"
                    onClick={() => setPreviewDoc(doc)}
                    data-testid={`open-preview-${doc.id}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      {getFileIcon(doc.name)}
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium text-[#2C2420] truncate max-w-[200px]">
                            {doc.name}
                          </p>
                          {doc.version > 1 && (
                            <span className="text-xs bg-[#4A3B32]/10 text-[#4A3B32] px-1.5 py-0.5 rounded shrink-0">
                              v{doc.version}
                            </span>
                          )}
                        </div>
                        {doc.expiryDate && (
                          <span className={cn(
                            "text-xs flex items-center gap-1 mt-0.5",
                            isExpired(doc.expiryDate) ? "text-red-600" :
                            isExpiringSoon(doc.expiryDate) ? "text-amber-600" : "text-[#8C8078]"
                          )}>
                            <Clock className="w-3 h-3" />
                            {isExpired(doc.expiryDate) ? 'Expired' : 'Expires'} {formatDate(doc.expiryDate)}
                          </span>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell">
                    <span className="text-xs px-2 py-1 rounded-full bg-[#4A3B32]/10 text-[#4A3B32]">
                      {doc.category}
                    </span>
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {doc.tags?.slice(0, 2).map(tag => (
                        <span key={tag} className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700">
                          {tag}
                        </span>
                      ))}
                      {doc.tags?.length > 2 && (
                        <span className="text-xs text-[#8C8078]">+{doc.tags.length - 2}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-[#5D524D] hidden sm:table-cell">
                    {formatFileSize(doc.size)}
                  </td>
                  <td className="px-4 py-3 hidden lg:table-cell">
                    <div className="text-sm text-[#5D524D]">{formatDate(doc.uploadedAt)}</div>
                    <div className="text-xs text-[#8C8078]">{doc.uploadedBy}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1">
                      {doc.name?.toLowerCase().endsWith('.pdf') && (
                        <Button variant="ghost" size="icon" onClick={() => handlePreview(doc)}
                          className="h-8 w-8" data-testid={`preview-doc-${doc.id}`}>
                          <Eye className="w-4 h-4" />
                        </Button>
                      )}
                      <Button variant="ghost" size="icon" onClick={() => handleDownload(doc)}
                        className="h-8 w-8" data-testid={`download-doc-${doc.id}`}>
                        <Download className="w-4 h-4" />
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8"
                            data-testid={`doc-menu-${doc.id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => setSendDoc(doc)}
                            data-testid={`send-doc-${doc.id}`}>
                            <Send className="w-4 h-4 mr-2" /> Send to client
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem onClick={() => setEditDoc({...doc})}
                            data-testid={`edit-doc-${doc.id}`}>
                            <Tag className="w-4 h-4 mr-2" /> Edit Details
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleNewVersion(doc)}>
                            <Upload className="w-4 h-4 mr-2" /> Upload New Version
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleViewVersions(doc)}>
                            <History className="w-4 h-4 mr-2" /> Version History
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem className="text-red-600" onClick={() => handleDelete(doc)}>
                            <Trash2 className="w-4 h-4 mr-2" /> Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Upload Modal */}
      <Dialog open={showUploadModal} onOpenChange={(open) => { if (!open) { setShowUploadModal(false); setPendingFile(null); setVersionDoc(null); } }}>
        <DialogContent className="max-w-lg" data-testid="upload-modal">
          <DialogHeader>
            <DialogTitle>{versionDoc ? `Upload New Version — ${versionDoc.name}` : 'Upload Document'}</DialogTitle>
            <DialogDescription>
              {pendingFile && <span className="font-medium">{pendingFile.name}</span>}
              {pendingFile && <span className="text-[#8C8078] ml-2">({formatFileSize(pendingFile.size)})</span>}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {!versionDoc && (aiLoading || aiSuggestion) && (
              <div
                className="flex items-start gap-2 px-3 py-2 rounded-lg bg-[#A86A3A]/[0.08] border border-[#A86A3A]/30 text-sm"
                data-testid="ai-suggestion-banner"
              >
                <Sparkles className="w-4 h-4 text-[#A86A3A] mt-0.5 shrink-0" />
                <div className="min-w-0 flex-1">
                  {aiLoading ? (
                    <p className="text-[#5D524D]">Auto-classifying filename…</p>
                  ) : (
                    <>
                      <p className="text-[#2C2420]">
                        <span className="font-medium">AI suggestion:</span>{' '}
                        <span data-testid="ai-suggestion-category">{aiSuggestion.category}</span>
                        {aiSuggestion.tags?.length > 0 && (
                          <span className="text-[#5D524D]">
                            {' '}· {aiSuggestion.tags.join(', ')}
                          </span>
                        )}
                        {aiSuggestion.confidence && (
                          <span className="ml-2 text-xs px-1.5 py-0.5 rounded bg-white border border-[#A86A3A]/30 text-[#A86A3A]">
                            {aiSuggestion.confidence}
                          </span>
                        )}
                      </p>
                      {aiSuggestion.reason && (
                        <p className="text-xs text-[#8C8078] mt-0.5 truncate" title={aiSuggestion.reason}>
                          {aiSuggestion.reason}
                        </p>
                      )}
                      <p className="text-xs text-[#8C8078] mt-0.5">
                        Adjust below if needed — you have final say.
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}
            <div>
              <label className="text-sm font-medium text-[#2C2420] mb-1 block">Category</label>
              <Select value={uploadCategory} onValueChange={setUploadCategory}>
                <SelectTrigger data-testid="upload-category">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            <div>
              <label className="text-sm font-medium text-[#2C2420] mb-1 block">Tags</label>
              <div className="flex flex-wrap gap-2">
                {PRESET_TAGS.map(tag => (
                  <button key={tag} type="button"
                    onClick={() => toggleTag(tag, setUploadTags, uploadTags)}
                    className={cn(
                      "text-xs px-2.5 py-1 rounded-full border transition-colors",
                      uploadTags.includes(tag)
                        ? "bg-[#4A3B32] text-white border-[#4A3B32]"
                        : "bg-white text-[#5D524D] border-[#E6E0D9] hover:border-[#4A3B32]"
                    )} data-testid={`tag-${tag.replace(/\s/g, '-').toLowerCase()}`}>
                    {tag}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="text-sm font-medium text-[#2C2420] mb-1 block">Expiry Date</label>
              <Input type="date" value={uploadExpiry} onChange={(e) => setUploadExpiry(e.target.value)}
                data-testid="upload-expiry" />
            </div>

            <div>
              <label className="text-sm font-medium text-[#2C2420] mb-1 block">Notes</label>
              <textarea
                value={uploadNotes}
                onChange={(e) => setUploadNotes(e.target.value)}
                className="w-full px-3 py-2 border border-[#E6E0D9] rounded-md text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-[#4A3B32]/20"
                placeholder="Optional notes about this document..."
                data-testid="upload-notes"
              />
            </div>

            {uploading && (
              <div className="w-full bg-[#E6E0D9] rounded-full h-2">
                <div className="bg-[#4A3B32] h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setShowUploadModal(false); setPendingFile(null); setVersionDoc(null); }}
                disabled={uploading}>
                Cancel
              </Button>
              <Button onClick={() => handleUpload(versionDoc?.parentDocId || versionDoc?.id || '')}
                disabled={uploading}
                className="bg-[#4A3B32] hover:bg-[#3A2B22] gap-2"
                data-testid="confirm-upload-btn">
                {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {uploading ? `Uploading ${uploadProgress}%` : (versionDoc ? 'Upload Version' : 'Upload')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Document Modal */}
      <Dialog open={!!editDoc} onOpenChange={(open) => { if (!open) setEditDoc(null); }}>
        <DialogContent className="max-w-lg" data-testid="edit-doc-modal">
          <DialogHeader>
            <DialogTitle>Edit Document Details</DialogTitle>
          </DialogHeader>
          {editDoc && (
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-[#2C2420] mb-1 block">Name</label>
                <Input value={editDoc.name} onChange={(e) => setEditDoc({...editDoc, name: e.target.value})}
                  data-testid="edit-doc-name" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2C2420] mb-1 block">Category</label>
                <Select value={editDoc.category} onValueChange={(v) => setEditDoc({...editDoc, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#2C2420] mb-1 block">Tags</label>
                <div className="flex flex-wrap gap-2">
                  {PRESET_TAGS.map(tag => (
                    <button key={tag} type="button"
                      onClick={() => setEditDoc({
                        ...editDoc,
                        tags: editDoc.tags?.includes(tag) ? editDoc.tags.filter(t => t !== tag) : [...(editDoc.tags || []), tag]
                      })}
                      className={cn(
                        "text-xs px-2.5 py-1 rounded-full border transition-colors",
                        editDoc.tags?.includes(tag)
                          ? "bg-[#4A3B32] text-white border-[#4A3B32]"
                          : "bg-white text-[#5D524D] border-[#E6E0D9] hover:border-[#4A3B32]"
                      )}>
                      {tag}
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[#2C2420] mb-1 block">Expiry Date</label>
                <Input type="date" value={editDoc.expiryDate || ''}
                  onChange={(e) => setEditDoc({...editDoc, expiryDate: e.target.value})} />
              </div>
              <div>
                <label className="text-sm font-medium text-[#2C2420] mb-1 block">Notes</label>
                <textarea value={editDoc.notes || ''}
                  onChange={(e) => setEditDoc({...editDoc, notes: e.target.value})}
                  className="w-full px-3 py-2 border border-[#E6E0D9] rounded-md text-sm resize-none h-20 focus:outline-none focus:ring-2 focus:ring-[#4A3B32]/20" />
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <Button variant="outline" onClick={() => setEditDoc(null)}>Cancel</Button>
                <Button onClick={handleUpdateDoc} className="bg-[#4A3B32] hover:bg-[#3A2B22]"
                  data-testid="save-doc-btn">
                  Save Changes
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Version History Modal */}
      <Dialog open={!!versionHistory} onOpenChange={(open) => { if (!open) setVersionHistory(null); }}>
        <DialogContent className="max-w-lg" data-testid="version-history-modal">
          <DialogHeader>
            <DialogTitle>Version History</DialogTitle>
            <DialogDescription>
              {versionHistory?.doc?.name} — {versionHistory?.versions?.length || 0} version(s)
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto">
            {versionHistory?.versions?.map((ver) => (
              <div key={ver.id} className="flex items-center justify-between p-3 rounded-lg border border-[#E6E0D9] hover:bg-[#F9F7F5]"
                data-testid={`version-${ver.id}`}>
                <div className="flex items-center gap-3 min-w-0">
                  {getFileIcon(ver.name)}
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#2C2420] truncate">{ver.name}</p>
                    <p className="text-xs text-[#8C8078]">
                      v{ver.version} — {formatDate(ver.uploadedAt)} by {ver.uploadedBy}
                    </p>
                    <p className="text-xs text-[#8C8078]">{formatFileSize(ver.size)}</p>
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => handleDownload(ver)} className="h-8 w-8 shrink-0">
                  <Download className="w-4 h-4" />
                </Button>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Preview */}
      <PDFPreviewModal
        isOpen={!!pdfPreview}
        onClose={() => { if (pdfPreview?.url) URL.revokeObjectURL(pdfPreview.url); setPdfPreview(null); }}
        pdfUrl={pdfPreview?.url}
        fileName={pdfPreview?.name}
      />

      {/* Send to client */}
      {sendDoc && (
        <SendDocumentDialog
          document={sendDoc}
          clientId={clientId}
          onClose={() => setSendDoc(null)}
        />
      )}

      {/* Bulk move dialog */}
      <Dialog open={showBulkMove} onOpenChange={(open) => { if (!open) setShowBulkMove(false); }}>
        <DialogContent className="max-w-md" data-testid="bulk-move-modal">
          <DialogHeader>
            <DialogTitle>Move {selectedIds.size} document{selectedIds.size > 1 ? 's' : ''}</DialogTitle>
            <DialogDescription>Pick a destination folder. Choose &ldquo;Unfiled&rdquo; to remove from any folder.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2 max-h-[400px] overflow-y-auto py-2">
            <button
              type="button"
              onClick={() => handleBulkMove(null)}
              disabled={bulkBusy}
              className="w-full text-left px-3 py-2 rounded-md border border-[#E6E0D9] hover:bg-[#F9F7F5] text-sm flex items-center gap-2"
              data-testid="bulk-move-unfiled"
            >
              <FolderOpen className="w-4 h-4 text-[#8C8078]" /> Unfiled (no folder)
            </button>
            {Object.values(folderMap)
              .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
              .map((f) => (
                <button
                  key={f.id}
                  type="button"
                  onClick={() => handleBulkMove(f.id)}
                  disabled={bulkBusy}
                  className="w-full text-left px-3 py-2 rounded-md border border-[#E6E0D9] hover:bg-[#F9F7F5] text-sm flex items-center gap-2"
                  data-testid={`bulk-move-target-${f.id}`}
                >
                  <FolderOpen className="w-4 h-4 text-[#A86A3A]" /> {f.name}
                  {f.parentId && folderMap[f.parentId] && (
                    <span className="text-xs text-[#8C8078]">
                      in {folderMap[f.parentId].name}
                    </span>
                  )}
                </button>
              ))}
          </div>
        </DialogContent>
      </Dialog>
      </div>

      {/* Inline preview pane — 3rd grid column on xl, overlay on smaller screens */}
      {previewDoc && (
        <DocumentPreviewPane
          document={previewDoc}
          clientId={clientId}
          token={token}
          onClose={() => setPreviewDoc(null)}
          onOpenFull={(d) => handlePreview(d)}
        />
      )}
    </div>
  );
}
